/**
 * Living-field organism — Lab 9 grit torus as one creature.
 * Used by /living-field.html lab and AntigravityCanvas (chat/landing/page).
 */

export type LivingFieldMode =
  | 'idle'
  | 'hear'
  | 'speak'
  | 'see'
  | 'screen'
  | 'search'
  | 'make'
  | 'think'
  | 'error'
  | 'ingest'

export interface LivingFieldParams {
  spin: number
  noise: number
  major: number
  minor: number
  smear: number
  gaze: number
  morph: number
}

interface Dot {
  i: number
  x: number
  y: number
  px: number
  py: number
}

interface Pt {
  x: number
  y: number
  z: number
  a: number
  copper: number
}

export const LIVING_FIELD_TARGET: Record<LivingFieldMode, LivingFieldParams> = {
  idle: {
    spin: 0.00028,
    noise: 0.006,
    major: 0.23,
    minor: 0.29,
    smear: 0.16,
    gaze: 0.38,
    morph: 0,
  },
  hear: { spin: 0.00012, noise: 0.004, major: 0.23, minor: 0.28, smear: 0.2, gaze: 0.22, morph: 1 },
  think: { spin: 0.00005, noise: 0.014, major: 0.2, minor: 0.26, smear: 0.1, gaze: 0.08, morph: 1 },
  search: {
    spin: 0.00018,
    noise: 0.01,
    major: 0.23,
    minor: 0.28,
    smear: 0.24,
    gaze: 0.14,
    morph: 1,
  },
  see: { spin: 0.00007, noise: 0.002, major: 0.26, minor: 0.24, smear: 0.1, gaze: 1, morph: 1 },
  speak: {
    spin: 0.00016,
    noise: 0.005,
    major: 0.24,
    minor: 0.22,
    smear: 0.18,
    gaze: 0.06,
    morph: 1,
  },
  ingest: {
    spin: 0.00006,
    noise: 0.002,
    major: 0.2,
    minor: 0.22,
    smear: 0.14,
    gaze: 0.1,
    morph: 1,
  },
  make: { spin: 0.0001, noise: 0.004, major: 0.21, minor: 0.27, smear: 0.12, gaze: 0.12, morph: 0 },
  screen: {
    spin: 0.00004,
    noise: 0.001,
    major: 0.22,
    minor: 0.24,
    smear: 0.2,
    gaze: 0.2,
    morph: 1,
  },
  error: {
    spin: 0.00055,
    noise: 0.028,
    major: 0.24,
    minor: 0.3,
    smear: 0.36,
    gaze: 0.55,
    morph: 1,
  },
}

const GOLDEN = Math.PI * (3 - Math.sqrt(5))
const COPPER = { r: 201, g: 95, b: 58 }
const ERROR_RED = { r: 220, g: 52, b: 48 }

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t
}

function ease(t: number): number {
  return t * t * (3 - 2 * t)
}

export interface LivingFieldEngineOptions {
  count?: number
}

export class LivingFieldEngine {
  private count: number
  private dots: Dot[]
  private params: LivingFieldParams = { ...LIVING_FIELD_TARGET.idle }
  private mode: LivingFieldMode = 'idle'
  private formMode: LivingFieldMode = 'idle'
  private width = 1
  private height = 1
  private simTime = 0
  private lastNow = 0
  private memory = 0
  private ember = 0
  private lookX = 0
  private lookY = 0
  private pointerX = 0
  private pointerY = 0
  private hasPointer = false
  private dark = true
  private audioLevel = 0
  private voiceGain = 1
  private scrollProgress = 0
  private anchorBias = 0.5
  private paused = false
  private calmMode = false

  constructor(opts?: LivingFieldEngineOptions) {
    this.count = Math.max(120, Math.floor(opts?.count ?? 1280))
    this.dots = Array.from({ length: this.count }, (_, i) => ({ i, x: 0, y: 0, px: 0, py: 0 }))
  }

  getMode(): LivingFieldMode {
    return this.mode
  }

  resize(width: number, height: number, count?: number): void {
    this.width = Math.max(1, Math.round(width))
    this.height = Math.max(1, Math.round(height))
    if (count !== undefined && count !== this.count) {
      this.count = Math.max(120, Math.floor(count))
      this.dots = Array.from({ length: this.count }, (_, i) => ({ i, x: 0, y: 0, px: 0, py: 0 }))
    }
  }

  setMode(next: LivingFieldMode): void {
    if (next === this.mode) return
    if (next !== 'idle' && next !== 'make') {
      this.formMode = next
      this.params.morph = Math.min(this.params.morph, 0.12)
    }
    this.mode = next
  }

  setPointer(nx: number | null, ny?: number | null): void {
    if (nx === null || ny === null || ny === undefined) {
      this.hasPointer = false
      return
    }
    this.hasPointer = true
    this.pointerX = nx
    this.pointerY = ny
  }

  setDark(dark: boolean): void {
    this.dark = dark
  }

  setAudioLevel(level: number): void {
    this.audioLevel = Math.max(0, Math.min(1, level))
  }

  /** Landing uses <1 so silk/membrane never dominates the hero. */
  setVoiceGain(gain: number): void {
    this.voiceGain = Math.max(0, Math.min(1, gain))
  }

  setScrollProgress(p: number): void {
    this.scrollProgress = Math.max(0, Math.min(1, p))
  }

  setAnchorBias(bias: number): void {
    this.anchorBias = Math.max(0, Math.min(1, bias))
  }

  setPaused(paused: boolean): void {
    this.paused = paused
  }

  /** Keep the field present but quieter while the user is composing or speaking. */
  setCalmMode(calm: boolean): void {
    this.calmMode = calm
  }

  private cx(): number {
    return this.width * (0.36 + this.anchorBias * 0.28)
  }

  private cy(): number {
    return this.height * 0.5
  }

  private voice(): number {
    const gain = this.voiceGain
    if (gain < 0.01) return 0
    if (this.audioLevel > 0.015) return Math.min(1, this.audioLevel * gain)
    if (this.mode !== 'speak') return 0
    // No manic LFO — wait for real mic / playback energy.
    return 0
  }

  private aim(): { x: number; y: number } {
    if (this.mode === 'screen') return { x: 0.56, y: -0.2 }
    if (this.mode === 'error') {
      return { x: Math.sin(this.simTime * 0.008) * 0.8, y: Math.cos(this.simTime * 0.01) * 0.5 }
    }
    if (this.hasPointer) return { x: this.pointerX, y: this.pointerY }
    return { x: 0, y: 0 }
  }

  private ink(): { r: number; g: number; b: number } {
    // Light surfaces need denser ink — #1A1C2E alone reads as noise at sub-pixel size.
    const base = this.dark ? { r: 255, g: 255, b: 255 } : { r: 18, g: 20, b: 34 }
    const err =
      this.mode === 'error' || this.formMode === 'error'
        ? this.mode === 'error'
          ? Math.max(this.params.morph, 0.35)
          : this.params.morph
        : 0
    if (err < 0.01) return base
    return {
      r: Math.round(lerp(base.r, ERROR_RED.r, err)),
      g: Math.round(lerp(base.g, ERROR_RED.g, err)),
      b: Math.round(lerp(base.b, ERROR_RED.b, err)),
    }
  }

  private trailInk(): { r: number; g: number; b: number } {
    const base = this.dark ? { r: 192, g: 192, b: 208 } : { r: 48, g: 54, b: 78 }
    const err =
      this.mode === 'error' || this.formMode === 'error'
        ? this.mode === 'error'
          ? Math.max(this.params.morph, 0.35)
          : this.params.morph
        : 0
    if (err < 0.01) return base
    return {
      r: Math.round(lerp(base.r, ERROR_RED.r, err * 0.85)),
      g: Math.round(lerp(base.g, ERROR_RED.g, err * 0.85)),
      b: Math.round(lerp(base.b, ERROR_RED.b, err * 0.85)),
    }
  }

  private torusPoint(i: number, t: number, p: LivingFieldParams, voice: number): Pt {
    const span = Math.min(this.width, this.height)
    const n = this.count
    const scroll = this.scrollProgress
    const major = span * p.major * (1 + this.memory * 0.06 + scroll * 0.04)
    const minor = major * p.minor * (1 + this.memory * 0.1)
    const theta = (i / n) * Math.PI * 2 * 3.0 + t * (p.spin + scroll * 0.00012)
    const phi = (i * GOLDEN) % (Math.PI * 2)
    const ring = Math.pow(Math.abs(Math.sin(phi)), 1.85)
    const shell = minor * (0.38 + ring * 0.82)
    const x3 = (major + Math.cos(phi) * shell) * Math.cos(theta)
    const y3 = (major + Math.cos(phi) * shell) * Math.sin(theta)
    const z3 = Math.sin(phi) * shell
    const nx = x3 + Math.sin(i * 1.7 + t * 0.0009) * span * p.noise
    const ny = y3 + Math.cos(i * 2.1 + t * 0.0008) * span * p.noise * 0.8
    const tiltX = 0.9 + this.lookY * 0.34 * p.gaze + scroll * 0.12
    const tiltZ = 0.46 + this.lookX * 0.52 * p.gaze
    const yTilt = ny * Math.cos(tiltX) - z3 * Math.sin(tiltX)
    const zA = ny * Math.sin(tiltX) + z3 * Math.cos(tiltX)
    const xTilt = nx * Math.cos(tiltZ) + zA * Math.sin(tiltZ)
    const zTilt = -nx * Math.sin(tiltZ) + zA * Math.cos(tiltZ)
    const persp = 1 + zTilt / (major * 4.2)
    const front = Math.max(0, Math.min(1, (zTilt / (minor * 1.7) + 1) * 0.5))
    const inner = Math.max(0, Math.min(1, Math.hypot(xTilt, yTilt) / (major + minor)))
    // Softer pupil falloff so the torus body reads as a creature, not sparse grit.
    const pupil = Math.pow(inner, 1.35)
    const breathe = 1 + Math.sin(t * 0.0012) * 0.012 + voice * 0.02 + scroll * 0.02
    const cx = this.cx()
    const cy = this.cy()
    const lightBoost = this.dark ? 1 : 1.35
    return {
      x: cx + xTilt * persp * breathe,
      y: cy + yTilt * persp * 0.86 * breathe,
      z: zTilt,
      a: Math.min(1, (0.18 + front * 0.72 + ring * 0.18 + this.memory * 0.12) * pupil * lightBoost),
      copper: this.ember * front * (i % 7 === 0 ? 0.95 : 0.18),
    }
  }

  private fromCenter(pt: Pt): { cx: number; cy: number; dx: number; dy: number; dist: number } {
    const cx = this.cx()
    const cy = this.cy()
    const dx = pt.x - cx
    const dy = pt.y - cy
    return { cx, cy, dx, dy, dist: Math.hypot(dx, dy) || 1 }
  }

  private warpHear(torus: Pt, t: number, voice: number): Pt {
    const { cx, cy, dx, dy, dist } = this.fromCenter(torus)
    const ripple = Math.sin(dist * 0.07 - t * 0.006) * (0.018 + voice * 0.055)
    const s = 1 + ripple
    return {
      x: cx + dx * s,
      y: cy + dy * s,
      z: torus.z * (1 + voice * 0.12),
      a: torus.a * (0.88 + voice * 0.35),
      copper: 0,
    }
  }

  private warpThink(torus: Pt): Pt {
    const { cx, cy, dx, dy, dist } = this.fromCenter(torus)
    const span = Math.min(this.width, this.height)
    const inner = Math.min(1, dist / (span * 0.11))
    return {
      x: cx + dx * 0.62,
      y: cy + dy * 0.62,
      z: torus.z * 0.5,
      a: torus.a * Math.pow(inner, 1.7) * 0.92,
      copper: 0,
    }
  }

  private warpSearch(torus: Pt, i: number, t: number): Pt {
    const span = Math.min(this.width, this.height)
    const n = this.count
    const { dx, dy, dist } = this.fromCenter(torus)
    const hunger = Math.pow(((i * 13) % n) / n, 1.35)
    const u = (t * 0.00013 + i * 0.0024) % 1
    const trip = u < 0.55 ? u / 0.55 : (1 - u) / 0.45
    const out = hunger * trip * span * 0.44
    return {
      x: torus.x + (dx / dist) * out,
      y: torus.y + (dy / dist) * out,
      z: torus.z * (1 - trip * 0.55),
      a: torus.a * (0.26 + (1 - trip) * 0.78) * (0.72 + this.memory * 0.4),
      copper: 0,
    }
  }

  private warpSee(torus: Pt): Pt {
    const span = Math.min(this.width, this.height)
    const { cx, cy, dx, dy, dist } = this.fromCenter(torus)
    const hole = span * 0.12
    const rim = dist < hole ? 0.035 : torus.a * 1.12
    return {
      x: cx + dx * 1.2 + this.lookX * span * 0.018,
      y: cy + dy * 1.2 + this.lookY * span * 0.014,
      z: torus.z * 1.05,
      a: rim,
      copper: 0,
    }
  }

  private roundedRectPerimeter(
    u: number,
    hw: number,
    hh: number,
    r: number
  ): { x: number; y: number; nx: number; ny: number } {
    const cr = Math.min(r, hw * 0.9, hh * 0.9)
    const w = (hw - cr) * 2
    const h = (hh - cr) * 2
    const arc = Math.PI * 0.5 * cr
    const per = 2 * w + 2 * h + 4 * arc
    let d = (((u % 1) + 1) % 1) * per
    if (d <= w) return { x: -hw + cr + d, y: -hh, nx: 0, ny: -1 }
    d -= w
    if (d <= arc) {
      const a = -Math.PI / 2 + (d / arc) * (Math.PI / 2)
      return {
        x: hw - cr + Math.cos(a) * cr,
        y: -hh + cr + Math.sin(a) * cr,
        nx: Math.cos(a),
        ny: Math.sin(a),
      }
    }
    d -= arc
    if (d <= h) return { x: hw, y: -hh + cr + d, nx: 1, ny: 0 }
    d -= h
    if (d <= arc) {
      const a = (d / arc) * (Math.PI / 2)
      return {
        x: hw - cr + Math.cos(a) * cr,
        y: hh - cr + Math.sin(a) * cr,
        nx: Math.cos(a),
        ny: Math.sin(a),
      }
    }
    d -= arc
    if (d <= w) return { x: hw - cr - d, y: hh, nx: 0, ny: 1 }
    d -= w
    if (d <= arc) {
      const a = Math.PI / 2 + (d / arc) * (Math.PI / 2)
      return {
        x: -hw + cr + Math.cos(a) * cr,
        y: hh - cr + Math.sin(a) * cr,
        nx: Math.cos(a),
        ny: Math.sin(a),
      }
    }
    d -= arc
    if (d <= h) return { x: -hw, y: hh - cr - d, nx: -1, ny: 0 }
    d -= h
    const a = Math.PI + (d / Math.max(arc, 0.001)) * (Math.PI / 2)
    return {
      x: -hw + cr + Math.cos(a) * cr,
      y: -hh + cr + Math.sin(a) * cr,
      nx: Math.cos(a),
      ny: Math.sin(a),
    }
  }

  private warpScreen(i: number, t: number): Pt {
    const span = Math.min(this.width, this.height)
    const hw = span * 0.4
    const hh = hw * (9 / 16)
    const u = (i / this.count + t * 0.000035) % 1
    const phi = (i * GOLDEN) % (Math.PI * 2)
    const rim = this.roundedRectPerimeter(u, hw, hh, span * 0.034)
    const tube = span * 0.016 * (0.55 + 0.45 * Math.abs(Math.cos(phi)))
    const x0 = rim.x + rim.nx * Math.cos(phi) * tube
    const y0 = rim.y + rim.ny * Math.cos(phi) * tube
    const z0 = Math.sin(phi) * tube * 1.1
    const tilt = 0.38
    const yTilt = y0 * Math.cos(tilt) - z0 * Math.sin(tilt)
    const zTilt = y0 * Math.sin(tilt) + z0 * Math.cos(tilt)
    const persp = 1 + zTilt / (span * 2.2)
    const front = 0.5 + 0.5 * Math.cos(phi)
    return {
      x: this.cx() + span * 0.04 + x0 * persp,
      y: this.cy() + yTilt * persp,
      z: zTilt,
      a: 0.18 + front * 0.72,
      copper: 0,
    }
  }

  private warpIngest(i: number, t: number): Pt {
    const span = Math.min(this.width, this.height)
    const hw = span * 0.2
    const hh = span * 0.3
    const cols = 26
    const rows = 38
    const col = i % cols
    const row = Math.floor(i / cols) % rows
    const u = col / (cols - 1)
    const v = row / (rows - 1)
    const edge = col === 0 || col === cols - 1 || row === 0 || row === rows - 1
    const x0 = (u - 0.5) * 2 * hw
    const y0 = (v - 0.5) * 2 * hh
    const sweep = (t * 0.00016) % 1
    const band = Math.abs(v - sweep) < 0.035
    const ingested = v < sweep
    const pull = ingested ? 0.28 : 0
    const x1 = x0 * (1 - pull)
    const y1 = y0 * (1 - pull * 0.9)
    const tilt = 0.18
    const z0 = (edge ? 8 : 0) + Math.sin(u * 6 + t * 0.001) * 2
    const yTilt = y1 * Math.cos(tilt) - z0 * Math.sin(tilt)
    const zTilt = y1 * Math.sin(tilt) + z0 * Math.cos(tilt)
    const persp = 1 + zTilt / (span * 2.8)
    return {
      x: this.cx() + x1 * persp,
      y: this.cy() + yTilt * persp,
      z: zTilt,
      a: band ? 0.95 : edge ? 0.7 : ingested ? 0.28 : 0.5,
      copper: 0,
    }
  }

  private warpError(torus: Pt, i: number, t: number): Pt {
    const tear = (i + Math.floor(t * 0.028)) % 9 === 0
    const jitter = tear ? Math.sin(t * 0.02 + i) * 6 : 0
    return {
      x: torus.x + jitter,
      y: torus.y + jitter * 0.6,
      z: torus.z,
      a: tear ? 0.04 : torus.a * 0.9,
      copper: 0,
    }
  }

  private speakWave(i: number, t: number, voice: number): Pt {
    const layers = 5
    const layer = i % layers
    const gain = [1, 0.78, 0.52, 0.32, 0.18][layer] ?? 0.14
    const u = i / Math.max(1, this.count - 1)
    const envelope = Math.pow(Math.sin(Math.max(0.002, Math.min(0.998, u)) * Math.PI), 1.45)
    const amp = this.height * (0.016 + voice * 0.155) * envelope
    const stagger = (layer - 2) * this.height * 0.009
    const y =
      Math.sin(u * Math.PI * 6 - t * 0.0038) * amp * gain +
      Math.sin(u * Math.PI * 11 + t * 0.0046) * amp * 0.22 * gain
    return {
      x: this.cx() + (u - 0.5) * this.width * 0.72,
      y: this.height * 0.52 + y + stagger,
      z: (2 - layer) * 18,
      a: (0.2 + (4 - layer) * 0.12 + voice * 0.3) * envelope,
      copper: layer <= 1 && i % 6 === 0 ? 0.4 + voice * 0.32 : 0,
    }
  }

  private warpForm(look: LivingFieldMode, torus: Pt, i: number, t: number, voice: number): Pt {
    if (look === 'hear') return this.warpHear(torus, t, voice)
    if (look === 'think') return this.warpThink(torus)
    if (look === 'search') return this.warpSearch(torus, i, t)
    if (look === 'see') return this.warpSee(torus)
    if (look === 'speak') return this.speakWave(i, t, voice)
    if (look === 'ingest') return this.warpIngest(i, t)
    if (look === 'screen') return this.warpScreen(i, t)
    if (look === 'error') return this.warpError(torus, i, t)
    return torus
  }

  private sample(i: number, t: number, p: LivingFieldParams): Pt {
    const voice = this.voice()
    const torus = this.torusPoint(i, t, p, voice)
    if (p.morph < 0.01 || this.formMode === 'idle') return torus
    const form = this.warpForm(this.formMode, torus, i, t, voice)
    const k = ease(p.morph)
    const burst =
      this.formMode === 'speak'
        ? Math.sin(k * Math.PI) * 0.03
        : this.formMode === 'screen' || this.formMode === 'ingest'
          ? Math.sin(k * Math.PI) * 0.1
          : Math.sin(k * Math.PI) * 0.045
    const midX = torus.x + (torus.x - this.cx()) * burst
    const midY = torus.y + (torus.y - this.cy()) * burst
    return {
      x: lerp(midX, form.x, k),
      y: lerp(midY, form.y, k),
      z: lerp(torus.z, form.z, k),
      a: lerp(torus.a, form.a, k),
      copper: lerp(torus.copper, form.copper, k),
    }
  }

  tick(nowMs: number, ctx: CanvasRenderingContext2D): void {
    if (!this.lastNow) this.lastNow = nowMs
    const dt = nowMs - this.lastNow
    this.lastNow = nowMs
    if (!this.paused) this.simTime += dt * (this.calmMode ? 0.2 : 1)

    const want = LIVING_FIELD_TARGET[this.mode]
    ;(Object.keys(this.params) as Array<keyof LivingFieldParams>).forEach(key => {
      const rate = this.paused ? 0 : key === 'morph' ? 0.026 : 0.045
      this.params[key] = lerp(this.params[key], want[key], rate)
    })

    if (!this.paused) {
      if (this.mode === 'search') this.memory = Math.min(1, this.memory + dt * 0.00028)
      else this.memory = Math.max(0, this.memory - dt * 0.000045)
      if (this.mode === 'make') this.ember = 1
      else this.ember = Math.max(0, this.ember - dt * 0.00035)
      const target = this.aim()
      this.lookX = lerp(this.lookX, target.x, 0.05)
      this.lookY = lerp(this.lookY, target.y, 0.05)
    }

    const bg = this.dark ? '0,0,0' : '249,250,251'
    // Lighter trails on light UI so smear doesn't erase the organism.
    const smear = this.dark ? 0.22 + this.params.smear * 0.35 : 0.12 + this.params.smear * 0.2
    ctx.fillStyle = `rgba(${bg},${smear})`
    ctx.fillRect(0, 0, this.width, this.height)

    const ordered = this.dots
      .map(d => {
        const s = this.sample(d.i, this.simTime, this.params)
        d.px = d.x || s.x
        d.py = d.y || s.y
        d.x = s.x
        d.y = s.y
        return { d, s }
      })
      .sort((a, b) => a.s.z - b.s.z)

    const dust = this.ink()
    const trail = this.trailInk()
    const trailAlpha = this.dark ? 0.22 : 0.38
    const sizeBoost = this.dark ? 1 : 1.35
    for (const { d, s } of ordered) {
      const alpha = Math.max(0, Math.min(1, s.a * (this.calmMode ? 0.58 : 1)))
      if (alpha < 0.025) continue
      ctx.beginPath()
      ctx.moveTo(d.px, d.py)
      ctx.lineTo(d.x, d.y)
      ctx.strokeStyle = `rgba(${trail.r},${trail.g},${trail.b},${alpha * trailAlpha})`
      ctx.lineWidth = this.dark ? 1 : 1.15
      ctx.stroke()
      const useCopper = s.copper > 0.28
      ctx.fillStyle = useCopper
        ? `rgba(${COPPER.r},${COPPER.g},${COPPER.b},${alpha})`
        : `rgba(${dust.r},${dust.g},${dust.b},${alpha})`
      const size = (0.85 + s.a * 1.35) * sizeBoost
      ctx.fillRect(d.x - size * 0.5, d.y - size * 0.5, size, size)
    }
  }
}
